import { RefObject, useState } from 'react'
import { FormikProps } from 'formik'

interface CopyFromSource<Source> {
  pickerOpen: boolean
  openPicker: () => void
  closePicker: () => void
  overwriteSource: Source | null
  cancelOverwrite: () => void
  confirmOverwrite: () => void
  handlePicked: (source: Source) => void
}

// Applying a source replaces what is in the form, so a dirty form asks before overwriting
const useCopyFromSource = <Source, Values>(
  formikRef: RefObject<FormikProps<Values>>,
  apply: (source: Source) => void
): CopyFromSource<Source> => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [overwriteSource, setOverwriteSource] = useState<Source | null>(null)

  const applySource = (source: Source) => {
    setPickerOpen(false)
    setOverwriteSource(null)
    apply(source)
  }

  const handlePicked = (source: Source) => {
    if (formikRef.current?.dirty) {
      setPickerOpen(false)
      setOverwriteSource(source)
    } else {
      applySource(source)
    }
  }

  return {
    pickerOpen,
    openPicker: () => setPickerOpen(true),
    closePicker: () => setPickerOpen(false),
    overwriteSource,
    cancelOverwrite: () => setOverwriteSource(null),
    confirmOverwrite: () => {
      if (overwriteSource) applySource(overwriteSource)
    },
    handlePicked
  }
}

export default useCopyFromSource
